import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LogOut, AlertTriangle, Home, RefreshCw } from 'lucide-react';
import PanelStrip from './PanelStrip';
import ActionInput from './ActionInput';
import ComicLoading from './ComicLoading';
import MissionComplete from './MissionComplete';
import { useTheme } from '../context/ThemeContext';
import { chatWithClaude, chatWithClaudeForComic, resetSession } from '../services/claude';
import { generatePanelImage, parseSceneForImage } from '../services/imageGeneration';
import SYMBOLS from '../utils/symbols';
import '../styles/ComicGame.css';

// Environment-aware logging - silent in production
const isDev = process.env.NODE_ENV !== 'production';
const log = (...args) => isDev && console.log(...args);

// Generate unique panel IDs
let panelIdCounter = 0;
const generatePanelId = () => `panel-${++panelIdCounter}`;

const ComicGame = ({ isInitializing, onGameReady, onReturnToMenu }) => {
  const [panels, setPanels] = useState([]);
  const [messages, setMessages] = useState([]); // Raw message history for Claude
  const [isLoading, setIsLoading] = useState(false);
  const [missionComplete, setMissionComplete] = useState(null);
  const [error, setError] = useState(null);
  const [verifiedEvent, setVerifiedEvent] = useState(null); // Store verified event info from server
  const [showEndConfirm, setShowEndConfirm] = useState(false); // End mission confirmation
  const [showHomeConfirm, setShowHomeConfirm] = useState(false); // Home/menu confirmation
  const [showNewMissionConfirm, setShowNewMissionConfirm] = useState(false); // New mission confirmation
  const [showBackConfirm, setShowBackConfirm] = useState(false); // Browser back button confirmation
  const [comicTitle, setComicTitle] = useState(null); // Generated comic book title
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  
  const { textScale } = useTheme();
  const zoomTimeoutRef = useRef(null);
  
  const gameContainerRef = useRef(null);
  const hasStartedRef = useRef(false); // Prevent double start

  // Generate a comic book title based on the story
  const generateComicTitle = useCallback(async (storyPanels, resultType) => {
    try {
      // Get the story content from panels
      const storyContent = storyPanels
        .filter(p => p.role === 'assistant' || p.playerAction)
        .map(p => p.content || p.playerAction)
        .join(' ')
        .substring(0, 1000); // Limit to first 1000 chars
      
      const response = await chatWithClaude([
        { 
          role: 'user', 
          content: `Based on this time travel adventure story, generate a short, punchy comic book title (3-6 words max). The title should be dramatic and evocative, like classic comic book titles. Just respond with the title, nothing else. Do not use any formatting or punctuation.

Story summary: ${storyContent}

Mission result: ${resultType}`
        }
      ]);
      
      // Clean up the response - remove quotes, markdown, extra punctuation
      const cleanTitle = response
        .replace(/\*+/g, '')           // Remove asterisks (markdown bold/italic)
        .replace(/_+/g, '')            // Remove underscores (markdown italic)
        .replace(/#+/g, '')            // Remove hash symbols (markdown headers)
        .replace(/`+/g, '')            // Remove backticks (markdown code)
        .replace(/^["']+|["']+$/g, '') // Remove wrapping quotes only (not apostrophes in words)
        .replace(/^title:\s*/i, '')    // Remove "Title:" prefix
        .trim()
        .toUpperCase();
      
      setComicTitle(cleanTitle);
    } catch (err) {
      if (isDev) console.error('Error generating comic title:', err);
      // Fallback title
      setComicTitle('A TIME TRAVELER\'S TALE');
    }
  }, []);

  // Handle end mission - always gives abandoned result
  const handleEndMission = useCallback(() => {
    setShowEndConfirm(false);
    const result = { 
      type: 'abandoned', 
      success: false, 
      message: 'Mission abandoned by the player.' 
    };
    setMissionComplete(result);
    generateComicTitle(panels, result.type);
  }, [panels, generateComicTitle]);

  // Generate image for a panel - returns success status
  const generateImageForPanel = useCallback(async (panelId, content, panelType, mood, year) => {
    try {
      log('Generating image for panel:', panelId);
      const result = await generatePanelImage(content, {
        year: year || verifiedEvent?.year || 2000,
        panelType: panelType,
        mood: mood
      });

      if (result.success) {
        // Update panel with image URL
        setPanels(prev => prev.map(panel => 
          panel.id === panelId
            ? { ...panel, imageUrl: result.imageUrl, isGeneratingImage: false }
            : panel
        ));
        return { success: true, imageUrl: result.imageUrl };
      } else {
        if (isDev) console.error('Image generation failed:', result.error);
        // Mark as error but don't break the game
        setPanels(prev => prev.map(panel => 
          panel.id === panelId
            ? { ...panel, imageError: true, isGeneratingImage: false }
            : panel
        ));
        return { success: false, error: result.error };
      }
    } catch (err) {
      if (isDev) console.error('Error in image generation:', err);
      setPanels(prev => prev.map(panel => 
        panel.id === panelId
          ? { ...panel, imageError: true, isGeneratingImage: false }
          : panel
      ));
      return { success: false, error: err.message };
    }
  }, [verifiedEvent]);

  // Parse mission completion from response
  const parseMissionCompletion = (response) => {
    const missionPatterns = [
      { pattern: /MISSION COMPLETE: PERFECT TIMELINE/i, type: 'perfect', success: true },
      { pattern: /MISSION COMPLETE: ALTERED TIMELINE/i, type: 'altered', success: true },
      { pattern: /MISSION COMPLETE: RULES BROKEN/i, type: 'rules_broken', success: true },
      { pattern: /MISSION COMPLETE: INNOVATIVE TIMELINE/i, type: 'innovative', success: true },
      { pattern: /MISSION FAILED: HISTORY UNCHANGED/i, type: 'failed', success: false },
      { pattern: /MISSION FAILED: TIMELINE CORRUPTED/i, type: 'corrupted', success: false }
    ];

    for (const { pattern, type, success } of missionPatterns) {
      if (pattern.test(response)) {
        return {
          type,
          success,
          message: response
        };
      }
    }
    return null;
  };

  // Start the game - now uses server-side event verification
  const startGame = useCallback(async () => {
    // Prevent double execution
    if (hasStartedRef.current) {
      log('Game already started, skipping...');
      return;
    }
    hasStartedRef.current = true;
    
    // Reset session for new mission
    resetSession();
    
    setIsLoading(true);
    setError(null);
    setVerifiedEvent(null);
    
    try {
      // Call server with empty messages - server handles event selection
      // The server now fetches verified events from Wikipedia API
      const result = await chatWithClaudeForComic([]);
      
      // Store verified event info from server response
      if (result.verifiedEvent) {
        log('Verified event from server:', result.verifiedEvent);
        setVerifiedEvent(result.verifiedEvent);
      }
      
      // Create the first panel (establishing shot)
      const panelId = generatePanelId();
      const sceneData = parseSceneForImage(result.message, { 
        year: result.verifiedEvent?.year || 2000 
      });
      
      const newPanel = {
        id: panelId,
        role: 'assistant',
        content: result.message,
        panelType: 'establishing',
        mood: sceneData.mood,
        isGeneratingImage: true,
        imageUrl: null,
        timestamp: Date.now()
      };

      setPanels([newPanel]);
      setMessages([{ role: 'assistant', content: result.message }]);
      
      // Generate image for the establishing shot and wait for it to complete
      await generateImageForPanel(
        panelId, 
        result.message, 
        'establishing', 
        sceneData.mood,
        result.verifiedEvent?.year
      );
      
      // Only show the game once the first panel is fully ready
      if (onGameReady) onGameReady();
    } catch (err) {
      if (isDev) console.error('Error starting game:', err);
      setError('Failed to initialize mission. Please try again.');
      hasStartedRef.current = false; // Allow retry on error
      // Still call onGameReady so user can see the error and retry
      if (onGameReady) onGameReady();
    } finally {
      setIsLoading(false);
    }
  }, [generateImageForPanel, onGameReady]);

  // Handle player action
  const handlePlayerAction = async (action) => {
    if (!action.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    // Prepare message with reminder
    const reminderText = ` [Remember: Prevent the event without revealing you're from the future or sacrificing yourself.]`;
    const messageWithReminder = action + reminderText;

    try {
      // Get Claude's response (session ID is managed automatically)
      const newMessages = [...messages, { role: 'user', content: messageWithReminder }];
      const response = await chatWithClaude(newMessages);

      // Check for mission completion
      const completion = parseMissionCompletion(response);
      
      // Determine panel type based on content
      const sceneData = parseSceneForImage(response, { 
        year: verifiedEvent?.year || 2000 
      });
      
      const panelType = completion ? 'splash' : sceneData.panelType;

      // Create assistant panel WITH the player action attached
      const assistantPanelId = generatePanelId();
      const assistantPanel = {
        id: assistantPanelId,
        role: 'assistant',
        content: response,
        playerAction: action, // Attach the player's action to this panel
        panelType,
        mood: sceneData.mood,
        isGeneratingImage: true,
        imageUrl: null,
        timestamp: Date.now()
      };

      // Update panels state
      const updatedPanels = [...panels, assistantPanel];
      setPanels(updatedPanels);
      setMessages([...newMessages, { role: 'assistant', content: response }]);

      // Generate image for the response
      generateImageForPanel(
        assistantPanelId, 
        response, 
        panelType, 
        sceneData.mood,
        verifiedEvent?.year
      );

      // Handle mission completion
      if (completion) {
        setTimeout(() => {
          setMissionComplete(completion);
          generateComicTitle(updatedPanels, completion.type);
        }, 2000); // Delay to let the final panel render
      }
    } catch (err) {
      if (isDev) console.error('Error processing action:', err);
      setError('Communication error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset game for new mission
  const handleNewMission = () => {
    panelIdCounter = 0;
    hasStartedRef.current = false; // Reset the started flag
    resetSession(); // Clear session for new mission
    setPanels([]);
    setMessages([]);
    setMissionComplete(null);
    setComicTitle(null);
    setError(null);
    setVerifiedEvent(null);
    startGame();
  };

  // Start game on mount - empty dependency array means it only runs once
  useEffect(() => {
    startGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - we only want this to run once on mount

  // Handle browser back button
  useEffect(() => {
    // Push a state to history so we can intercept the back button
    window.history.pushState({ gameActive: true }, '');

    const handlePopState = (e) => {
      // User pressed back button - show confirmation instead of navigating
      e.preventDefault();
      
      // Push state again to stay on the page
      window.history.pushState({ gameActive: true }, '');
      
      // Show confirmation dialog (only if game is in progress and not already complete)
      if (panels.length > 0 && !missionComplete) {
        setShowBackConfirm(true);
      } else {
        // If no progress or mission complete, just go back
        onReturnToMenu();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [panels.length, missionComplete, onReturnToMenu]);

  // Show zoom indicator when text scale changes
  useEffect(() => {
    setShowZoomIndicator(true);
    
    // Clear any existing timeout
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }
    
    // Hide after 1.5 seconds
    zoomTimeoutRef.current = setTimeout(() => {
      setShowZoomIndicator(false);
    }, 1500);
    
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, [textScale]);

  return (
    <div className="comic-game" ref={gameContainerRef}>
      {/* Zoom indicator */}
      {showZoomIndicator && (
        <div className="zoom-indicator">
          <span className="zoom-icon">
            <span className="zoom-icon-small">A</span>
            <span className="zoom-icon-large">a</span>
          </span>
          <span className="zoom-value">{Math.round(textScale * 100)}%</span>
        </div>
      )}

      {/* Loading overlay for initial load */}
      {isInitializing && <ComicLoading />}

      {/* Main game content */}
      {!isInitializing && (
        <>
          {/* Comic strip - scrollable panel area */}
          <div className="comic-strip-area">
            <PanelStrip 
              panels={panels}
              autoScrollToLatest={true}
              actionArea={
                <div className="action-area">
                  {error && (
                    <div className="error-message">
                      <AlertTriangle size={16} strokeWidth={2.5} />
                      {error}
                    </div>
                  )}
                  
                  <ActionInput
                    onSubmit={handlePlayerAction}
                    disabled={isLoading || !!missionComplete}
                    isLoading={isLoading}
                    placeholder={missionComplete ? "Mission complete!" : "What do you do?"}
                  />

                  {/* Mission control buttons */}
                  <div className="mission-buttons">
                    {/* Home button */}
                    <button 
                      className="mission-btn home-btn"
                      onClick={() => setShowHomeConfirm(true)}
                      disabled={isLoading}
                    >
                      <Home size={16} strokeWidth={2.5} />
                      <span className="btn-text">Main Menu</span>
                    </button>

                    {/* New Mission button */}
                    <button 
                      className="mission-btn new-mission-btn"
                      onClick={() => setShowNewMissionConfirm(true)}
                      disabled={isLoading}
                    >
                      <RefreshCw size={16} strokeWidth={2.5} />
                      <span className="btn-text">New Mission</span>
                    </button>

                    {/* End Mission button */}
                    {!missionComplete && panels.length > 0 && (
                      <button 
                        className="mission-btn end-mission-btn"
                        onClick={() => setShowEndConfirm(true)}
                        disabled={isLoading}
                      >
                        <LogOut size={16} strokeWidth={2.5} />
                        <span className="btn-text">End Mission</span>
                      </button>
                    )}
                  </div>

                  {/* Swipe hint - shows when second panel is created */}
                  {panels.length === 2 && !panels[1]?.isGeneratingImage && (
                    <div className="swipe-hint">
                      <span className="hint-icon">{SYMBOLS.ARROW_LEFT}</span>
                      <span className="hint-text">Swipe to navigate panels</span>
                      <span className="hint-icon">{SYMBOLS.ARROW_RIGHT}</span>
                    </div>
                  )}
                </div>
              }
            />
          </div>

          {/* End Mission Confirmation Dialog */}
          {showEndConfirm && (
            <div className="confirm-overlay">
              <div className="confirm-dialog">
                <h3 className="confirm-title">End Mission?</h3>
                <p className="confirm-text">
                  Are you sure you want to end this mission? Your result will be based on your progress so far.
                </p>
                <div className="confirm-buttons">
                  <button 
                    className="confirm-btn confirm-cancel"
                    onClick={() => setShowEndConfirm(false)}
                  >
                    Keep Playing
                  </button>
                  <button 
                    className="confirm-btn confirm-end"
                    onClick={handleEndMission}
                  >
                    End Mission
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Home Confirmation Dialog */}
          {showHomeConfirm && (
            <div className="confirm-overlay">
              <div className="confirm-dialog home-dialog">
                <h3 className="confirm-title">Return to Main Menu?</h3>
                <p className="confirm-text">
                  Are you sure you want to leave? Your current mission progress will be lost.
                </p>
                <div className="confirm-buttons">
                  <button 
                    className="confirm-btn confirm-cancel"
                    onClick={() => setShowHomeConfirm(false)}
                  >
                    Keep Playing
                  </button>
                  <button 
                    className="confirm-btn confirm-home"
                    onClick={onReturnToMenu}
                  >
                    Main Menu
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Mission Confirmation Dialog */}
          {showNewMissionConfirm && (
            <div className="confirm-overlay">
              <div className="confirm-dialog new-mission-dialog">
                <h3 className="confirm-title">Start New Mission?</h3>
                <p className="confirm-text">
                  Are you sure you want to abandon this mission and start a new one?
                </p>
                <div className="confirm-buttons">
                  <button 
                    className="confirm-btn confirm-cancel"
                    onClick={() => setShowNewMissionConfirm(false)}
                  >
                    Keep Playing
                  </button>
                  <button 
                    className="confirm-btn confirm-new-mission"
                    onClick={() => {
                      setShowNewMissionConfirm(false);
                      handleNewMission();
                    }}
                  >
                    New Mission
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Browser Back Button Confirmation Dialog */}
          {showBackConfirm && (
            <div className="confirm-overlay">
              <div className="confirm-dialog back-dialog">
                <h3 className="confirm-title">Leave Game?</h3>
                <p className="confirm-text">
                  Are you sure you want to exit? Your current mission progress will be lost.
                </p>
                <div className="confirm-buttons">
                  <button 
                    className="confirm-btn confirm-cancel"
                    onClick={() => setShowBackConfirm(false)}
                  >
                    Keep Playing
                  </button>
                  <button 
                    className="confirm-btn confirm-back"
                    onClick={onReturnToMenu}
                  >
                    Exit Game
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mission Complete Overlay */}
          {missionComplete && (
            <MissionComplete
              result={missionComplete}
              panels={panels}
              comicTitle={comicTitle}
              onNewMission={handleNewMission}
              onReturnToMenu={onReturnToMenu}
            />
          )}
        </>
      )}

      {/* Decorative page edges */}
      <div className="page-edge left"></div>
      <div className="page-edge right"></div>
    </div>
  );
};

export default ComicGame;